import { Subject, fromEvent } from 'rxjs';
import getArticleSectionCommand from '../services/articleSearchParametersService';

const articleCardCarouselSettingsSubject = new Subject();

/**
 * Retrieves the query selector for a specific carousel element.
 *
 * This function extracts the `data-article-card-carousel` attribute from the provided element
 * and constructs a query selector string for it. If the attribute is not present, it returns `null`.
 *
 * @param {HTMLElement} carouselElement - The carousel element to extract the selector from.
 * @returns {string|null} The query selector string for the carousel, or `null` if the attribute is missing.
 *
 * @example
 * const carouselElement = document.querySelector('[data-article-card-carousel="carousel1"]');
 * const querySelector = getArticleCardCarouselQuerySelector(carouselElement);
 * console.log(querySelector); // '[data-article-card-carousel="carousel1"]'
 */
const getArticleCardCarouselQuerySelector = function (carouselElement) {
  const carouselIdentifier = carouselElement.getAttribute('data-article-card-carousel');
  return carouselIdentifier ? `[data-article-card-carousel="${carouselIdentifier}"]` : null;
};

/**
 * Determines whether arrow controls should be added to the given carousel element.
 *
 * This function checks if the carousel element already contains an element
 * with the `[data-article-card-carousel-arrow]` attribute. If such an element
 * is not found, it returns `true`, indicating that arrow controls should be added.
 * Otherwise, it returns `false`.
 *
 * @param {HTMLElement} carouselElement - The carousel DOM element to check.
 * @returns {boolean} `true` if arrow controls should be added, `false` otherwise.
 */
const shouldAddArticleCardCarouselArrows = function (carouselElement) {
  const arrowFlag = carouselElement.querySelector('[data-article-card-carousel-arrow]');
  return !arrowFlag;
};

/**
 * Loads the settings for all article card carousels on the page.
 *
 * This function selects all elements with the `data-article-card-carousel` attribute,
 * extracts their search parameters and query selectors, and emits the settings
 * for each carousel through the `articleCardCarouselSettingsSubject`.
 */
const loadArticleCardCarouselSettings = function() {
  const articleCardsCarouselElements = document.querySelectorAll('[data-article-card-carousel]');

  articleCardsCarouselElements.forEach((carouselSection) => {
    const articleSectionCommandElement = carouselSection.querySelector('[data-article-section-command]');
    
    const carouselSetting = {
      carouselQuerySelector: getArticleCardCarouselQuerySelector(carouselSection),
      articleSectionCommand: getArticleSectionCommand(articleSectionCommandElement),
      shouldAddArrows: shouldAddArticleCardCarouselArrows(carouselSection),
    };

    // Emit the carousel settings
    articleCardCarouselSettingsSubject.next(carouselSetting);
  });
};

/**
 * Observable that emits when the document content is fully loaded.
 * THIS IS TO AVOID TRY TO READ THE DOM BEFORE IT IS FULLY LOADED
 * This observable listens for the `DOMContentLoaded` event and triggers the
 * `loadArticleCardCarouselSettings` function to initialize the carousel settings.
 */
const onDocumentContentLoaded$ = fromEvent(document, 'DOMContentLoaded');
onDocumentContentLoaded$.subscribe(loadArticleCardCarouselSettings);
const onArticleCardCarouselSettingsReady$ = articleCardCarouselSettingsSubject.asObservable();

export default onArticleCardCarouselSettingsReady$;